/**
 * Shared formatting utilities for player data display
 */

import { differenceInMonths } from "date-fns";

/**
 * Formats a date string to a localized date format
 * @param dateString - The date string to format
 * @returns Formatted date string or "N/A" if invalid
 */
export const formatContractDate = (dateString?: string): string => {
  if (!dateString || dateString === "N/A") return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "N/A";
  }
};

/**
 * Formats a numeric value as currency in millions
 * @param value - The numeric value to format
 * @returns Formatted currency string or "N/A" if invalid
 */
export const formatCurrency = (value?: number | null): string => {
  if (!value || value === 0) return "N/A";
  return `€${(value / 1000000).toFixed(1)}M`;
};

/**
 * Formats a numeric value as a percentage
 * @param value - The numeric value to format (decimal or percentage)
 * @returns Formatted percentage string or "N/A" if invalid
 */
export const formatPercentage = (value?: number | null): string => {
  if (value === undefined || value === null) return "N/A";
  const percentage = value < 1 ? value * 100 : value;
  return `${Math.round(percentage)}%`;
};

/**
 * Formats a decimal value as a rounded-sm integer
 * @param value - The numeric value to format
 * @returns Formatted string or "N/A" if invalid
 */
export const formatDecimal = (value?: number | null): string => {
  if (value === undefined || value === null) return "N/A";
  return Math.round(value).toString();
};

/**
 * Computes the number of months remaining on a player's contract from today.
 * Returns 0 when the contract has already expired or no expiry date is provided.
 * @param contractExpires - ISO date string of contract expiration
 * @returns Remaining months (floored to 0)
 */
export const getContractRemainingMonths = (
  contractExpires?: string | null
): number =>
  contractExpires
    ? Math.max(0, differenceInMonths(new Date(contractExpires), new Date()))
    : 0;
